import { useEffect, useState } from 'react'
import classes from '../styles.module.scss'
import { useShared } from '../../../../contexts/shared.context';
import { IMCB, IScreeningMCBCreditSupport, IScreeningMCBSubjects, IScreeningRegisteredSubjects } from '../../../../types/screening.types';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useKato } from '../../../../contexts/kato.context';
import { updateScreening } from '../../../../requests/screening.request';
import FooterComponent from '../../components/footer.component';
import Field from '../../../../components/field/field.component';
import { isEmpty, t } from '../../../../helpers/utils';
import { toast } from 'react-toastify';
import { PlusIcon } from '../../../../assets/icons';

const baseClass = classes['screening-tab__smb-development'];
const translationKey = 'screening.smb-development';

const defaultSmbDevelopment: IMCB = {
    mcbSubjects: {
        total: '',
        TOO: '',
        IP: '',
        KH: '',
        SPK: '',
    },
    registeredSubjects: {
        juridical: '',
        agricultural: '',
        construction: '',
        retail: '',
        printing: '',
        finance: '',
        commerce: '',
    },
    totalSum: '',
    creditSupport: {
        subsidizing_projects: '',
        subsidizing_sum: '',
        guarantee_projects: '',
        guarantee_sum: '',
        grant_projects: '',
        grant_sum: '',
    }
}

const SmbDevelopmentTab = () => {
    const { selectedInfoYear } = useShared();
    const { kato } = useParams<{ kato: string }>();
    const { i18n: { language } } = useTranslation();
    const { screeningInfo: { smbDevelopment }, updateScreeningInfo } = useKato();
    const [formData, setFormData] = useState<IMCB>(defaultSmbDevelopment);
    const [errors, setErrors] = useState<Record<string, boolean>>({});

    const [isAdding, setIsAdding] = useState(false);
    const [newField, setNewField] = useState<any | null>(null);

    const handleSave = () => {
        if (validateFields() && kato) {
            updateScreening(+kato, 'smbDevelopment', formData, selectedInfoYear)
                .then(() => {
                    updateScreeningInfo();
                    toast.success(t(`admin.success`, language));
                })
                .catch((error) => {
                    console.error('Error updating agriculture:', error);
                });
        }
    };

    const validateFields = () => {
        const newErrors: Record<string, boolean> = {};

        // Проверка mcbSubjects
        if (formData.mcbSubjects) {
            const entries = Object.entries(formData.mcbSubjects);
            const allEmpty = entries.every(([, val]) => isEmpty(val));
            const someFilled = entries.some(([, val]) => !isEmpty(val));

            if (someFilled && !allEmpty) {
                entries.forEach(([key, val]) => {
                    if (isEmpty(val)) {
                        newErrors[key] = true;
                    }
                });
            }
        }

        // Проверка registeredSubjects
        if (formData.registeredSubjects) {
            const entries = Object.entries(formData.registeredSubjects);
            const allEmpty = entries.every(([, val]) =>
                typeof val !== 'object' ? isEmpty(val) : isEmpty(val.value)
            );
            const someFilled = entries.some(([, val]) =>
                typeof val !== 'object' ? !isEmpty(val) : !isEmpty(val.value)
            );

            if (someFilled && !allEmpty) {
                entries.forEach(([key, val]) => {
                    const value = typeof val !== 'object' ? val : val.value;
                    if (isEmpty(value)) {
                        newErrors[key] = true;
                    }
                });
            }
        }

        // Проверка totalSum
        if (!isEmpty(formData.totalSum)) {
            if (isNaN(Number(formData.totalSum))) {
                newErrors['totalSum'] = true;
            }
        }

        // Проверка creditSupport
        if (formData.creditSupport) {
            const entries = Object.entries(formData.creditSupport);
            const allEmpty = entries.every(([, val]) => isEmpty(val));
            const someFilled = entries.some(([, val]) => !isEmpty(val));

            if (someFilled && !allEmpty) {
                entries.forEach(([key, val]) => {
                    if (isEmpty(val)) {
                        newErrors[key] = true;
                    }
                });
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleStartAdd = () => {
        setNewField(
            {
                labelRu: '',
                labelKz: '',
                value: '',
            }
        );
        setIsAdding(true);
    };

    const handleCancelAdd = () => {
        setNewField(null);
        setIsAdding(false);
    };

    const handleSaveNewField = () => {
        if (!validateNewField() || !formData.registeredSubjects) return;

        setFormData({
            ...formData,
            registeredSubjects: {
                ...formData.registeredSubjects,
                [`createdAt-${Date.now()}`]: {
                    ...newField
                }
            },
        });
        setNewField(null);
        setIsAdding(false);
    };

    const validateNewField = () => {
        const newErrors = {} as Record<string, boolean>;
        if (!newField) return false;
        Object.keys(newField).forEach(field => {
            if (isEmpty(newField[field])) {
                newErrors[`new-field-${field}`] = true;
            }
        });
        setErrors(newErrors);
        return Object.values(newErrors).every(error => !error);
    };


    useEffect(() => {
        if (smbDevelopment) {
            setFormData(smbDevelopment || defaultSmbDevelopment);
        } else {
            setFormData(defaultSmbDevelopment);
        }
    }, [smbDevelopment]);

    const renderFields = (lang: 'ru' | 'kz') => {
        const suffix = lang === 'ru' ? 'Ru' : 'Kz';
        return (
            <div className={classes['fields-container']}>
                {
                    formData.mcbSubjects && <div className={classes['field-block']}>
                        <div className={classes['field-block__title']}>{t(`${translationKey}.mcb-subjects.title`, lang)}</div>
                        {Object.entries(formData.mcbSubjects).map(([key, value]) => {
                            const val = value === '' ? '' : +(value.toString());

                            return (
                                <Field
                                    key={key}
                                    label={t(`${translationKey}.mcb-subjects.${key}`, lang)}
                                    value={val ?? ''}
                                    onChange={(newVal) => {
                                        const val = newVal === '' ? '' : +newVal;
                                        setFormData({
                                            ...formData,
                                            mcbSubjects: {
                                                ...formData.mcbSubjects as IScreeningMCBSubjects,
                                                [key]: val
                                            }
                                        });
                                    }}
                                    error={errors[key]}
                                    required
                                    type={'number'}
                                />
                            )
                        })}
                    </div>
                }

                {
                    formData.registeredSubjects && <div className={classes['field-block']}>
                        <div className={classes['field-block__title']}>{t(`${translationKey}.registered-subjects.title`, lang)}</div>
                        {
                            Object.entries(formData.registeredSubjects).map(([fieldKey, value]) => {
                                const isCreated = fieldKey.includes('created');
                                const val =
                                    isCreated
                                        ? value?.value === '' || value?.value == null
                                            ? ''
                                            : +value.value
                                        : value === '' || value == null
                                            ? ''
                                            : +value;
                                const label = isCreated ? value[`label${suffix}`] : t(`${translationKey}.registered-subjects.${fieldKey}`, lang);

                                return (
                                    <Field
                                        key={fieldKey}
                                        label={label}
                                        value={val ?? ''}
                                        onChange={(newVal) => {
                                            const val = newVal === '' ? '' : +newVal;

                                            setFormData({
                                                ...formData,
                                                registeredSubjects: {
                                                    ...formData.registeredSubjects as IScreeningRegisteredSubjects,
                                                    [fieldKey]: isCreated
                                                        ? {
                                                            ...formData.registeredSubjects?.[fieldKey],
                                                            value: val
                                                        }
                                                        : val
                                                }
                                            });
                                        }}
                                        error={errors[fieldKey]}
                                        type={'number'}
                                        required
                                    />
                                )
                            })
                        }

                        {!isAdding && (
                            <div className={classes['field-block__actions']}>
                                <button onClick={handleStartAdd}><PlusIcon /> {t(`actions.add`, lang)}</button>
                            </div>
                        )}

                        {isAdding && newField && (
                            <div className={classes['field-block']}>
                                <div className={classes['field-block__fields']}>
                                    <Field
                                        key={`label${suffix}`}
                                        label={`${t(`${translationKey}.registered-subjects.label`, lang)}`}
                                        value={newField[`label${suffix}`]}
                                        onChange={(value) => {
                                            const val = value;
                                            setNewField({ ...newField, [`label${suffix}`]: val });
                                        }}
                                        error={errors[`new-field-label${suffix}`]}
                                        type={'text'}
                                        required
                                    />
                                    <Field
                                        key={`value`}
                                        label={`${t(`${translationKey}.registered-subjects.value`, lang)}`}
                                        value={newField[`value`]}
                                        onChange={(value) => {
                                            const val = value;
                                            setNewField({ ...newField, [`value`]: val });
                                        }}
                                        error={errors[`new-field-value`]}
                                        type={'number'}
                                        required
                                    />
                                </div>
                                <div className={classes['field-block__actions']}>
                                    <button onClick={handleSaveNewField}>{t(`actions.save`, lang)}</button>
                                    <button onClick={handleCancelAdd}>{t(`actions.cancel`, lang)}</button>
                                </div>
                            </div>
                        )}
                    </div>
                }

                {
                    formData.totalSum && <div className={classes['field-block']}>
                        <Field
                            label={t(`${translationKey}.total-sum`, lang)}
                            value={formData.totalSum === '' ? '' : +(formData.totalSum.toString())}
                            onChange={(newVal) => {
                                const val = newVal === '' ? '' : +newVal;
                                setFormData({
                                    ...formData,
                                    totalSum: val
                                });
                            }}
                            error={errors['totalSum']}
                            required
                        />
                    </div>
                }

                {
                    formData.creditSupport && <div className={classes['field-block']}>
                        <div className={classes['field-block__title']}>{t(`${translationKey}.credit-support.title`, lang)}</div>
                        {
                            ['subsidizing', 'guarantee', 'grant'].map((key) => {
                                return (
                                    <div className={classes['field-block']} key={key}>
                                        <div className={classes['field-block__title']}>{t(`${translationKey}.credit-support.${key}`, lang)}</div>
                                        {['projects', 'sum'].map((subKey) => {
                                            const fieldKey = `${key}_${subKey}` as keyof IScreeningMCBCreditSupport;
                                            const value = formData.creditSupport && (formData.creditSupport[fieldKey] === '' ? '' : +(formData.creditSupport[fieldKey].toString()));
                                            return (<Field
                                                key={fieldKey}
                                                label={t(`${translationKey}.credit-support.${subKey}`, lang)}
                                                value={value ?? ''}
                                                onChange={(newVal) => {
                                                    const val = newVal === '' ? '' : +newVal;
                                                    setFormData({
                                                        ...formData,
                                                        creditSupport: {
                                                            ...formData.creditSupport as IScreeningMCBCreditSupport,
                                                            [fieldKey]: val
                                                        }
                                                    });
                                                }}
                                                error={errors[fieldKey]}
                                                required
                                            />)
                                        })}
                                    </div>
                                )
                            })
                        }
                    </div>
                }
            </div>
        )
    };

    return (
        <div className={classes[baseClass]}>
            <div className={classes[`grid`]}>
                {renderFields(language as 'ru' | 'kz')}
                {renderFields(language === 'ru' ? 'kz' : 'ru')}
            </div>

            <FooterComponent
                onSave={handleSave}
                validateFields={validateFields}
            />
        </div>
    )
}

export default SmbDevelopmentTab